import { Button } from 'components/Button';
import { CPFInput } from 'components/Inputs/CPFInput';
import { MaskedInput } from 'components/Inputs/MaskedInput';
import { TextInput } from 'components/Inputs/TextInput';
import { useEmployees } from 'hooks/useEmployees';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { employeeValidation } from 'schemas';
import {
  EmployeeDefaultValues,
  IEmployee,
  IEmployeeCreate,
} from 'types/Employee';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
} from '@mui/material';

type IEmployeeDialogForm = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  id?: string;
  employee?: IEmployee;
};

export const EmployeeDialogForm: React.FC<IEmployeeDialogForm> = ({
  title,
  isOpen,
  onClose,
  id,
  employee,
}) => {
  const form = useForm({
    defaultValues: employee ?? EmployeeDefaultValues,
    resolver: employeeValidation(),
  });

  const { handleOnSubmit, loadingCreate } = useEmployees();

  const onSubmit = async (values: IEmployeeCreate) => {
    const isValid = await handleOnSubmit(values);

    if (!isValid) {
      return;
    }
    form.reset();
    onClose();
  };

  useEffect(() => {
    if (employee) {
      form.reset(employee);
      return;
    }
    form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 0 }}>
        <Grid container spacing={2}>
          <Grid item>
            <FormProvider {...form}>
              <Box p={2}>
                <Grid container alignItems="flex-start" spacing={3}>
                  <Grid item container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <TextInput
                        required
                        fullWidth
                        name="name"
                        id="name"
                        label="NOME COMPLETO"
                        disabled={Boolean(id)}
                        inputProps={{ maxLength: 60 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextInput
                        required
                        fullWidth
                        id="email"
                        name="email"
                        label="E-MAIL"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <CPFInput
                        required
                        id="cpf"
                        name="cpf"
                        disabled={Boolean(id)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <MaskedInput
                        id="phone"
                        name="phone"
                        label="CELULAR"
                        mask="(##) #####-####"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </FormProvider>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 0 }}>
        <Box pb={2} pr={4}>
          <Grid container item spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => {
                  onClose();
                  form.reset();
                }}
              >
                Cancelar
              </Button>
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                disabled={loadingCreate}
                onClick={form.handleSubmit(onSubmit)}
              >
                Salvar
                {loadingCreate && (
                  <CircularProgress
                    size="small"
                    sx={{ width: 24, height: 24, ml: 2 }}
                  />
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
